import { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, Button, Stack, Paper, Chip
} from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SaveIcon from '@mui/icons-material/Save';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ExamsRadiologyReport from "../radiology/reviewReport/examsRadiologyReport";
import DiagnosisRadiologyReport from "../radiology/reviewReport/diagnosisRadiologyReport";
import { useDispatch, useSelector } from "react-redux";
import { getParentPatientRequest, getStudiesImagesRequest, resetPostReport } from "../../../redux/modules/report/slice";
import { toast } from "react-toastify";
import PatientInfo from "./patientInfo";
import { USER_ROLE_DEFAULT } from "../../../constants/Constant";

// Step configuration - reduced to 2 steps
const STEPS = [
  { label: 'Exam Details', key: 'examDetails', icon: AssignmentIcon },
  { label: 'Findings & Report', key: 'diagnosis', icon: EditNoteIcon }
];

const ReportForm = ({ itemData, setItemData, setShowComponent }) => {
  const dispatch = useDispatch();
  let { userData } = useSelector(state => state.auth);
  let { postReportRes, putReportRes } = useSelector(state => state.report);

  const [currentStep, setCurrentStep] = useState(1);
  const [saveType, setSaveType] = useState(1);

  // Calculate completion status for each step
  const stepCompletion = useMemo(() => ({
    examDetails: !!(
      itemData?.reportData?.completedExam?.length ||
      itemData?.reportData?.clinicalInfo?.trim()
    ),
    diagnosis: !!(
      itemData?.reportData?.findings?.trim() ||
      itemData?.reportData?.radiographicImpression?.trim()
    )
  }), [itemData]);

  useEffect(() => {
    if (itemData?.studyData?.orthancStudyInstanceUID) {
      return;
    }
    if (!itemData?.parentPatientResError) {
      dispatch(getParentPatientRequest({
        radiologyId: itemData?.radiologyId,
        orthancPatientId: itemData?.orthancPatientId,
        orderId: itemData?.orderId
      }));
    }
  }, [dispatch, itemData?.studyData?.orthancStudyInstanceUID, itemData?.orderId]);

  // Fetch study images once we have the study identifiers
  useEffect(() => {
    const hasStudyId = itemData?.studyData?.orthancStudyID || itemData?.studyData?.orthancStudyInstanceUID;
    if (hasStudyId && !itemData?.studyImages) {
      dispatch(getStudiesImagesRequest({
        orthancStudyID: itemData?.studyData?.orthancStudyID,
        studyInstanceUID: itemData?.studyData?.orthancStudyInstanceUID
      }));
    }
  }, [dispatch, itemData?.studyData?.orthancStudyID, itemData?.studyData?.orthancStudyInstanceUID, itemData?.studyImages]);

  useEffect(() => {
    if (postReportRes) {
      if (postReportRes?.success) {
        setItemData({
          ...itemData,
          reportData: postReportRes.data
        });
        if (saveType === "exit") {
          // Navigate to report summary if report was submitted (completed), otherwise back to list
          if (postReportRes.data?.reportStatus === 'completed') {
            setShowComponent("viewReport");
          } else {
            setShowComponent(null);
          }
        } else if (saveType === "next") {
          handleNext();
        }
      }
      toast(postReportRes?.message);
      dispatch(resetPostReport());
    }
    if (putReportRes) {
      if (putReportRes?.success) {
        setItemData({
          ...itemData,
          reportData: putReportRes.data
        });
        if (saveType === "exit") {
          if (putReportRes.data?.reportStatus === 'completed') {
            setShowComponent("viewReport");
          } else {
            setShowComponent(null);
          }
        } else if (saveType === "next") {
          handleNext();
        }
      }
      toast(putReportRes?.message);
      dispatch(resetPostReport());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, postReportRes, putReportRes, saveType, setShowComponent]);

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex) => {
    setCurrentStep(stepIndex + 1);
  };

  const handleSaveAndExit = () => {
    setSaveType("exit");
    setShowComponent(null);
  };

  const isPatient = userData.role === USER_ROLE_DEFAULT.PATIENT;

  return (
    <Box sx={{ maxWidth: '100%' }}>
      {/* Header with Professional Stepper */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Title and Save & Exit */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          {!isPatient && (
            <Box>
              <Typography
                variant="h5"
                sx={{
                  color: "text.primary",
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}
              >
                {itemData?.reportData?._id ? 'Edit Report' : 'Create Report'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {itemData?.seletedpatient ? `${itemData.seletedpatient.fName} ${itemData.seletedpatient.lName}` : ''}
              </Typography>
            </Box>
          )}
          {!isPatient && (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<SaveIcon />}
              onClick={handleSaveAndExit}
              sx={{
                whiteSpace: 'nowrap',
                minWidth: { xs: '100%', sm: 'auto' },
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  bgcolor: 'primary.lighter'
                }
              }}
            >
              Save & Exit
            </Button>
          )}
        </Stack>

        {/* Professional 2-Step Stepper */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1, sm: 2 }
        }}>
          {STEPS.map((step, index) => {
            const isCompleted = stepCompletion[step.key];
            const isActive = currentStep === index + 1;
            const StepIcon = step.icon;

            return (
              <Box key={step.key} sx={{ display: 'flex', alignItems: 'center' }}>
                {/* Step Button */}
                <Box
                  onClick={() => handleStepClick(index)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    bgcolor: isActive ? 'primary.main' : isCompleted ? 'success.lighter' : 'grey.100',
                    border: '2px solid',
                    borderColor: isActive ? 'primary.main' : isCompleted ? 'success.main' : 'transparent',
                    '&:hover': {
                      bgcolor: isActive ? 'primary.dark' : isCompleted ? 'success.light' : 'grey.200',
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  {/* Step Number/Icon */}
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: isActive ? 'rgba(255,255,255,0.2)' : isCompleted ? 'success.main' : 'grey.300',
                      color: isActive ? '#fff' : isCompleted ? '#fff' : 'grey.600',
                    }}
                  >
                    {isCompleted && !isActive ? (
                      <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <StepIcon sx={{ fontSize: 18 }} />
                    )}
                  </Box>

                  {/* Step Label */}
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: isActive ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                        fontSize: '0.7rem',
                        lineHeight: 1
                      }}
                    >
                      Step {index + 1}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: isActive ? '#fff' : isCompleted ? 'success.dark' : 'text.primary',
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                </Box>

                {/* Connector Line */}
                {index < STEPS.length - 1 && (
                  <Box
                    sx={{
                      width: { xs: 24, sm: 60 },
                      height: 3,
                      mx: 1,
                      borderRadius: 1,
                      bgcolor: stepCompletion[step.key] ? 'success.main' : 'grey.300',
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Patient Info */}
      {itemData?.seletedpatient && !isPatient && (
        <Box sx={{ marginBottom: 3 }}>
          <PatientInfo patient={itemData?.seletedpatient} />
        </Box>
      )}

      {/* Step Content */}
      <Box sx={{ minHeight: '400px' }}>
        {currentStep === 1 && (
          <ExamsRadiologyReport
            itemData={itemData}
            onNext={handleNext}
            setSaveType={setSaveType}
          />
        )}
        {currentStep === 2 && (
          <DiagnosisRadiologyReport
            itemData={itemData}
            onBack={handleBack}
            setSaveType={setSaveType}
            onReportSubmitted={() => setShowComponent(null)}
          />
        )}
      </Box>
    </Box>
  );
};

export default ReportForm;
